import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import agGridConfig from '../../../agGridConfig';
import config from '../../../config';

import columnDefinitions from '../../../shared/columnDefinitions/bookToTaxCodes';
import useSelectAgGridRow from '../../../shared/hooks/useSelectAgGridRow.hook';
import { useAddBookToTaxCode, useImportBookToTaxCodes, useRemoveBookToTaxCode, useUpdateBookToTaxCode, useValidateBookToTaxCodes } from '../../../shared/mutations/bookToTaxCodes';
import { useBookToTaxCodes } from '../../../shared/queries/bookToTaxCodes';
import { currentPeriodSelector, environmentSuffixSelector, periodOrCurrentSelector } from '../../../shared/store/selectors';
import { Period } from '../../../shared/types';
import currentPeriodValidator from '../../../shared/xlsxImport/validators/currentPeriod.validator';

import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../../userData/store/selectors';

import { normalizeCellToString } from '../../../utils/readXlsxFile.utils';
import AdminImportTab from '../shared/adminImportTab.component';
import useAddEditModalControls from '../shared/useAddEditModalControls';
import { useAlertState } from '../../../shared/hooks/useAlertState';
import useRemoveModalControls from '../shared/useRemoveModalControls';
import useUploadEntities, { TWithValidationError } from '../shared/useUploadEntities';

import { BookToTaxCode, BookToTaxCodeFormData, SaveEntityFunction } from '../types';
import AddEditModal from './addBookToTaxCodeModal.component';
import BookToTaxCodesAdminColumnRendererFactory from './bookToTaxCodesAdminColumnRenderer.component';
import DeleteBookToTaxCodeDescriptionModal from './deleteBookToTaxCodeModal.component';
import downloadBookToTaxCodeXlsx from '../../common/bookToTaxCodes/downloadBookToTaxCodeXlsx';

import columnHeaders from './xlsxImport/columnHeaders';
import { FIELD_MAPPINGS, MAX_COLUMNS_NUMBER, MIN_COLUMNS_NUMBER } from './xlsxImport/constants';


type BookToTaxCodeDescriptionsPageProps = {
  period: Period,
  currentPeriod: Period,
  canModify: boolean,
  hideDecimals: boolean,
  shouldDisplayZero: boolean,
  environmentSuffix: string,
  negativeMinus: boolean,
}


const BookToTaxCodeDescriptionsPage = ({
  canModify,
  period,
  currentPeriod,
  hideDecimals,
  shouldDisplayZero,
  environmentSuffix,
  negativeMinus,
}: BookToTaxCodeDescriptionsPageProps) => {
  const {
    selectAgGridRow,
    selectAgGridRowByIndex,
  } = useSelectAgGridRow(agGridConfig.adminClientAdministrationBookToTaxCodes);

  const {
    hideModalForAddEdit,
    showModalForAdd,
    showModalForEdit,
    entityToEdit,
    isAddEditModalVisible,
  } = useAddEditModalControls<BookToTaxCode>();

  const {
    hideModalForRemove,
    showModalForRemove,
    entityToRemove,
    rowToRemoveIndex,
    isRemoveModalVisible,
  } = useRemoveModalControls<BookToTaxCode>();

  const {
    isFetching: isFetchingEntities,
    data: entities,
    refetch: fetchEntities,
  } = useBookToTaxCodes({ periodName: period.name });
  const { mutate: addEntity, isLoading: isAddingEntity } = useAddBookToTaxCode();
  const { mutate: updateEntity, isLoading: isUpdatingEntity } = useUpdateBookToTaxCode();
  const { mutate: removeEntity, isLoading: isRemovingEntity } = useRemoveBookToTaxCode(() => {
    hideModalForRemove();
    selectAgGridRowByIndex(rowToRemoveIndex ? rowToRemoveIndex - 1 : 0);
  });

  const onSaveSuccess = useCallback((formData, { keepOpenAfterSaving, callback }) => {
    if (!keepOpenAfterSaving) {
      hideModalForAddEdit();
      selectAgGridRow(formData);
    }

    if (callback) {
      return callback();
    }
  }, [hideModalForAddEdit, selectAgGridRow]);

  const saveEntity: SaveEntityFunction<BookToTaxCodeFormData> = useCallback(
    async (formData, openForEdit, options) => {
      if (openForEdit) {
        updateEntity(
          { ...formData, periodName: period.name },
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      } else {
        addEntity(
          { ...formData, periodName: period.name },
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      }
    }, [addEntity, updateEntity, onSaveSuccess, period]
  );

  const isSavingEntity = isAddingEntity || isUpdatingEntity;

  const generateTemplateFileName = useCallback(
    ({ periodName }) => `TLS_Book_To_Tax_Codes_${periodName}`,
    []
  );

  const idFieldComparator = useCallback(
    (bookToTaxCodeDescription, importedBookToTaxCodeDescription) =>
      bookToTaxCodeDescription.tagCode === importedBookToTaxCodeDescription.tagCode
      && bookToTaxCodeDescription.countryTaxType === importedBookToTaxCodeDescription.countryTaxType
    , []);

  const { mutate: validateEntities, isLoading: isValidatingEntities } = useValidateBookToTaxCodes();

  const {
    alert,
    showAlert,
    hideAlert,
  } = useAlertState();

  const {
    isUploadingEntities,
    uploadEntities,
    uploadedEntities,
    clearUploadedEntities,
    areImportsPending,
  } = useUploadEntities<BookToTaxCode, { currentPeriod: Period }>(
    { validateEntities: validateEntities as any, isValidatingEntities },
    {
      sheetName: 'Book To Tax Code Descriptions',
      lastColumnLetter: 'H',
      columnHeaders,
      fieldMappings: FIELD_MAPPINGS,
      fieldNormalizers: {
        tagcode: normalizeCellToString,
      },
      minColumnsNumber: MIN_COLUMNS_NUMBER,
      maxColumnsNumber: MAX_COLUMNS_NUMBER,
      sortingField: 'tagCode',
      additionalValidators: [currentPeriodValidator],
      validationData: {
        currentPeriod,
      },
    },
    showAlert
  );

  const {
    mutate: importEntities,
    isLoading: isImportingEntities,
  } = useImportBookToTaxCodes(() => clearUploadedEntities());


  const adminColumnRenderer = useMemo(() => BookToTaxCodesAdminColumnRendererFactory({
    showModalForEdit,
    showModalForRemove,
  }), [
    showModalForEdit,
    showModalForRemove,
  ]);

  return (
    <AdminImportTab<
      BookToTaxCode,
      BookToTaxCodeFormData,
      TWithValidationError<BookToTaxCode>,
      undefined
    >
      pageTitle="Book to Tax Code Descriptions"

      canModify={canModify}

      AddEditEntityModal={AddEditModal}
      RemoveEntityModal={DeleteBookToTaxCodeDescriptionModal}
      adminColumnRenderer={adminColumnRenderer}
      addEntityMessage="Add Book to Tax Code"
      columnDefs={columnDefinitions}
      exportToExcel={downloadBookToTaxCodeXlsx}
      generateTemplateFileName={generateTemplateFileName}
      templatePath={config.templates.BOOK_TO_TAX_CODE_DESCRIPTIONS}
      comparatorConfig={{
        idField: 'tagCode',
        idFieldComparator,
      }}

      isFetchingEntities={isFetchingEntities}
      fetchEntities={fetchEntities}
      entities={entities}

      saveEntity={saveEntity}
      isSavingEntity={isSavingEntity}

      removeEntity={removeEntity}
      isRemovingEntity={isRemovingEntity}

      period={period}

      uploadEntities={uploadEntities}
      isUploadingEntities={isUploadingEntities}
      uploadedEntities={uploadedEntities}
      areImportsPending={areImportsPending}

      importEntities={importEntities}
      isImportingEntities={isImportingEntities}

      alert={alert}
      hideAlert={hideAlert}
      hideDecimals={hideDecimals}
      shouldDisplayZero={shouldDisplayZero}
      environmentSuffix={environmentSuffix}
      negativeMinus={negativeMinus}


      isAddEditModalVisible={isAddEditModalVisible}
      entityToEdit={entityToEdit}

      isRemoveModalVisible={isRemoveModalVisible}
      entityToRemove={entityToRemove}
      rowToRemoveIndex={rowToRemoveIndex}

      showModalForAdd={showModalForAdd}
      hideModalForAddEdit={hideModalForAddEdit}
      hideModalForRemove={hideModalForRemove}
    />
  );
};


const mapStateToProps = (state: { shared: any, user: any }) => ({
  period: periodOrCurrentSelector(state),
  currentPeriod: currentPeriodSelector(state),

  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),
});

export default connect(mapStateToProps)(BookToTaxCodeDescriptionsPage);
